import { useMemo, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { dateTimeNoSeconds } from '../../../shared/dateTime';
import { useStatuses } from '../statusDictionary';
import { knownVehicles } from '../changeHistoryFormat';
import { CHANGES_LIMIT, orderTimeline, type TimelineItem, type TimelineKind } from './timelineItems';
import { useFeature } from '../../featureFlags/FeatureGate';
import { GANTT_WINDOW_BRACKETS } from '../../fleet/fleetFeatures';
import { recordBrackets } from '../../fleet/bracketRules';
import { windowText } from '../bankRowText';

/**
 * Oś czasu zlecenia (sekcja 6 makiety FTL) — punkty z dziennika zmian, okna
 * klienta i „teraz”; reguła w `timelineItems.ts`. Pionowa lista od
 * najstarszego: przy 1366 px czyta się lepiej niż pozioma oś z kilkudziesięcioma
 * punktami. Długą oś zwijamy do ostatnich punktów — wcześniejsze na żądanie.
 */

/**
 * Okna punktów z tymi samymi nazwami co nawiasy na wykresach (24.10.2026,
 * S-24.10-05, `ganttWindowBrackets`): zamiast „Okno załadunku / rozładunku
 * u klienta” — Z1, R1, R2… z oknem przy punkcie (termin z planu TMS, bez
 * niego okno klienta; reguła `bracketRules.recordBrackets`). Tylko odczyt.
 */
export function withBracketItems(
  items: readonly TimelineItem[],
  record: { fromDate?: unknown; toDate?: unknown; keyPoints?: unknown },
  now: Date,
): TimelineItem[] {
  const brackets = recordBrackets(record).filter((b) => b.from && !Number.isNaN(new Date(b.from).getTime()));
  const rest = items.filter((i) => i.key !== 'plan-loading' && i.key !== 'plan-unloading');
  const added: TimelineItem[] = brackets.map((b) => ({
    key: `plan-bracket-${b.label}`,
    at: b.from as string,
    kind: 'plan',
    title: `${b.label} · okno ${b.kind === 'loading' ? 'załadunku' : 'rozładunku'}${b.place ? ` — ${b.place}` : ''}`,
    detail: `${windowText(b.from, b.to)} (${b.source === 'tmsPlan' ? 'termin z planu TMS' : 'okno zlecenia od klienta'})`,
    author: null,
    source: null,
    reason: null,
    planned: new Date(b.from as string).getTime() > now.getTime(),
  }));
  // Ten sam porządek co oś: po chwili, w tej samej chwili — kolejność dodania.
  return [...rest, ...added]
    .map((item, order) => ({ item, order, time: new Date(item.at).getTime() }))
    .sort((a, b) => a.time - b.time || a.order - b.order)
    .map((x) => x.item);
}

/** Ile punktów przed „teraz” pokazujemy od razu. */
export const TIMELINE_VISIBLE_PAST = 8;

const KIND_COLORS: Record<TimelineKind, string> = {
  record: 'text.disabled',
  status: 'primary.main',
  vehicle: 'success.main',
  alert: 'warning.main',
  trade: 'secondary.main',
  data: 'text.disabled',
  plan: 'info.main',
  now: 'error.main',
};

const KIND_LABELS: Record<TimelineKind, string> = {
  record: 'wpis',
  status: 'stan',
  vehicle: 'pojazd',
  alert: 'alert',
  trade: 'giełda i przewoźnicy',
  data: 'dane',
  plan: 'okno klienta',
  now: 'teraz',
};

const Dot = ({ item }: { item: TimelineItem }) => (
  <Box
    aria-hidden
    sx={{
      width: 10,
      height: 10,
      mt: '5px',
      borderRadius: '50%',
      flexShrink: 0,
      border: 2,
      borderColor: KIND_COLORS[item.kind],
      bgcolor: item.planned ? 'transparent' : KIND_COLORS[item.kind],
    }}
  />
);

const TimelineRow = ({ item }: { item: TimelineItem }) => {
  if (item.kind === 'now') {
    return (
      <Box component="li" data-testid="timeline-now" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Teraz · {dateTimeNoSeconds(item.at)}
        </Typography>
        <Box sx={{ flexGrow: 1, borderTop: 1, borderColor: 'error.main' }} />
      </Box>
    );
  }
  const meta = [item.author, item.source].filter(Boolean).join(' · ');
  return (
    <Box
      component="li"
      data-testid={`timeline-${item.kind}`}
      sx={{ display: 'grid', gridTemplateColumns: '130px 14px minmax(0, 1fr)', columnGap: 1, py: 0.5 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {dateTimeNoSeconds(item.at)}
      </Typography>
      <Dot item={item} />
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: item.kind === 'data' || item.kind === 'record' ? 'text.secondary' : undefined }}>
            {item.title}
          </Typography>
          {item.kind === 'plan' ? (
            <Chip size="small" variant="outlined" label={item.planned ? 'plan' : 'okno klienta'} sx={{ height: 20 }} />
          ) : null}
        </Stack>
        {item.detail ? (
          <Typography variant="caption" color="text.secondary" component="p">
            {item.detail}
          </Typography>
        ) : null}
        {meta || item.reason ? (
          <Typography variant="caption" color="text.secondary" component="p">
            {[meta, item.reason ? `powód: ${item.reason}` : null].filter(Boolean).join(' · ')}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export const OrderTimeline = ({ now }: { now?: Date }) => {
  const r = useRecordContext();
  const states = useStatuses();
  const [showAll, setShowAll] = useState(false);
  const brackets = useFeature(GANTT_WINDOW_BRACKETS);
  const items = useMemo(() => {
    if (!r) return [];
    const at = now ?? new Date();
    const base = orderTimeline(r, { states, vehicles: knownVehicles(r as Record<string, unknown>) }, at);
    return brackets ? withBracketItems(base, r as Record<string, unknown>, at) : base;
  }, [r, states, now, brackets]);
  if (!r) return null;
  const nowIndex = items.findIndex((i) => i.kind === 'now');
  const hiddenCount = showAll ? 0 : Math.max(0, nowIndex - TIMELINE_VISIBLE_PAST);
  const visible = items.slice(hiddenCount);
  const changes = Array.isArray(r.changes) ? r.changes.length : 0;
  const kinds = Array.from(new Set(items.map((i) => i.kind))).filter((k) => k !== 'now');
  return (
    <Card variant="outlined" data-testid="order-timeline">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
          Oś czasu zlecenia
        </Typography>
        {hiddenCount > 0 ? (
          <Button size="small" onClick={() => setShowAll(true)} sx={{ mb: 1 }}>
            Pokaż wcześniejsze ({hiddenCount})
          </Button>
        ) : null}
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {visible.map((item) => (
            <TimelineRow key={item.key} item={item} />
          ))}
        </Box>
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
          {kinds.map((k) => (
            <Stack key={k} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: KIND_COLORS[k] }} />
              <Typography variant="caption" color="text.secondary">
                {KIND_LABELS[k]}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Z dziennika zmian zlecenia (szczegóły pól — „Historia zmian” niżej) oraz okna klienta. Zmiany danych
          z jednej chwili są złączone w jeden punkt.
          {changes >= CHANGES_LIMIT ? ` Pokazujemy ${CHANGES_LIMIT} najnowszych wpisów dziennika.` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
};
